import {ProjectProperties} from "../ProjectProperties";
import {Product} from "../Product";
import {ProductionRateTable} from "./ProductionRateTable";

/**
 * Status of a per-item production-rate fetch.
 *  - NONE: never fetched (item has always had quantity 0)
 *  - LOADING: request in flight
 *  - READY: rates cached and available
 *  - ERROR: server error; computeCost falls back to 0
 */
export type ProductionRatesStatus = 'NONE' | 'LOADING' | 'READY' | 'ERROR';

/**
 * User-facing pricing state derived from {@link ProductionRatesStatus} +
 * quantity + rate-table content. Drives the cart's per-line price
 * rendering (spinner vs price vs "unavailable" vs "no rate for this
 * quantity") and the checkout-button enable/disable gate.
 *
 *  - `ZERO`: item is in the cart at quantity 0 — nothing to price.
 *  - `PENDING`: quantity > 0 and rates haven't finished loading yet
 *    (`NONE` or `LOADING`). UI shows a spinner instead of `$0.00`.
 *  - `READY`: quantity > 0, rates loaded, current quantity resolves to a
 *    numeric price at some facility.
 *  - `INFEASIBLE`: quantity > 0, rates loaded, table has feasible
 *    ranges BUT none of them cover the current quantity. UI shows
 *    "Price unavailable for this quantity — try 1-4 or 10+".
 *  - `ERROR`: quantity > 0, rates loaded (or errored) but no facility
 *    in the table carries any authored break at all — no way for the
 *    user to reach a valid quantity from here. UI shows "Price
 *    unavailable" + Retry.
 *
 * `INFEASIBLE` and `ERROR` both block checkout; `PENDING` blocks
 * checkout too (the total would be wrong). `ZERO` and `READY` are
 * the only checkout-safe states for a line the user intends to buy.
 */
export type ItemPricingState = 'ZERO' | 'PENDING' | 'READY' | 'INFEASIBLE' | 'ERROR';

export abstract class Item {
  public quantity: number;
  cost: number;
  tagId: number;
  useInventory: boolean = false;
  inventoryFacilityId: number = 0;

  /** Cached per-facility production rate table. Populated lazily by OrderService. */
  productionRates: ProductionRateTable;
  productionRatesStatus: ProductionRatesStatus = 'NONE';
  /** Quantity before the most recent update — used to detect 0→>0 transitions. */
  previousQuantity: number = 0;
  /** Facility chosen for production. 0 means "let the system pick the cheapest". */
  selectedFacilityId: number = 0;

  /**
   * Opaque server-issued signature for the production-rate row used to compute
   * `cost`. Captured at `computeCost()` time; echoed in the place-order envelope
   * under `signatures.productionRates`. Empty when rates haven't been fetched or
   * the server hasn't started emitting signatures yet (Shape B fallback uses
   * `ProductionRateTable.tableSignature` from the cache).
   */
  productionRateSignature: string = '';
  /**
   * Facility id whose row produced the current `cost`. May differ from
   * `selectedFacilityId` when the user has left selection at 0 (cheapest) and
   * the cheapest changed due to a quantity nudge. Used to keep
   * `productionRateSignature` paired with the row that priced this line.
   */
  costFacilityId: number = 0;

  constructor() {
    this.quantity = 0;
    this.cost = 0;
  }

  quantityChanged() {
    this.computeCost();
  }

  getCost(): number {
    if ( this.quantity === 0 ) {
      this.cost = 0;
    } else {
      this.computeCost();
    }
    return this.cost;
  }
  getQuantity(): number {
    return this.quantity;
  }

  /**
   * Compute cost from the cached `productionRates` table for the selected
   * facility (or the cheapest facility if none selected). When rates are not
   * yet available, cost is 0; the caller should refresh once rates load.
   *
   * As a side effect, also captures the `productionRateSignature` for the row
   * that priced this line so the cart can echo it in the place-order envelope.
   */
  computeCost() {
    if (this.quantity === 0) {
      this.cost = 0;
      this.costFacilityId = 0;
      this.productionRateSignature = '';
      return;
    }
    if (!this.productionRates || this.productionRatesStatus !== 'READY') {
      // Rates not yet loaded — leave cost at 0; will be recomputed when rates arrive.
      this.cost = 0;
      this.costFacilityId = 0;
      this.productionRateSignature = '';
      return;
    }
    let facilityId = this.selectedFacilityId;
    if (!facilityId) {
      const cheapest = this.productionRates.getCheapestFacility(this.quantity);
      facilityId = cheapest ? cheapest.facilityId : 0;
    }
    if (!facilityId) {
      this.cost = 0;
      this.costFacilityId = 0;
      this.productionRateSignature = '';
      return;
    }
    const r = this.productionRates.getCostForFacility(facilityId, this.quantity);
    this.cost = r ? (r.unitCost * this.quantity + r.perOrderCost) : 0;
    this.costFacilityId = facilityId;
    this.productionRateSignature = this.productionRates.getSignatureForFacility(facilityId, this.quantity) || '';
  }

  /**
   * Per-facility cost variation summary for the current quantity, sorted by
   * total cost ascending. Returns an empty array when rates aren't loaded yet
   * or only one feasible facility exists (UI hint suppressed in that case).
   */
  getFacilityCostSummary(): Array<{ facilityId: number; facilityName?: string; totalCost: number }> {
    if (!this.productionRates || this.productionRatesStatus !== 'READY' || this.quantity === 0) {
      return [];
    }
    const all = this.productionRates.getFacilityCosts(this.quantity);
    if (all.length < 2) { return []; }
    // Only surface the hint when the prices actually differ.
    const cheapest = all[0].totalCost;
    const allEqual = all.every(f => Math.abs(f.totalCost - cheapest) < 0.005);
    if (allEqual) { return []; }
    return all;
  }

  /**
   * Detailed per-facility breakdown (unit cost, per-order flat, line total)
   * for the current quantity. Returned in cheapest-first order. Used by the
   * Stage-1 cart-page price block when more than one facility prices the
   * item differently — surfaces actionable detail rather than just a flat
   * "From $X" hint. Returns an empty array when rates aren't loaded yet,
   * the quantity is zero, or every feasible facility agrees on price
   * within $0.005 (no meaningful variation to surface).
   */
  getFacilityCostBreakdown(): Array<{
    facilityId: number;
    facilityName?: string;
    unitCost: number;
    perOrderCost: number;
    totalCost: number;
  }> {
    if (!this.productionRates || this.productionRatesStatus !== 'READY' || this.quantity === 0) {
      return [];
    }
    const all = this.productionRates.getFacilityCostsDetailed(this.quantity);
    if (all.length < 2) { return []; }
    const cheapest = all[0].totalCost;
    const allEqual = all.every(f => Math.abs(f.totalCost - cheapest) < 0.005);
    if (allEqual) { return []; }
    return all;
  }

  /**
   * Quantity-break ladder for the facility that produced the current
   * `cost`. Each row carries `isCurrent: true` for the break the current
   * quantity falls into. Used by the Stage-1 cart-page "buy more to
   * save" hint so the user can see the unit cost at each tier.
   *
   * Returns an empty array when rates aren't loaded yet, the quantity
   * is zero, no costing facility has been resolved, or there is only
   * a single break (no quantity-based variation to surface).
   */
  getCurrentFacilityQuantityBreaks(): Array<{
    minQty: number;
    maxQty: number;
    unitCost: number;
    perOrderCost: number;
    isCurrent: boolean;
  }> {
    if (!this.productionRates || this.productionRatesStatus !== 'READY' || this.quantity === 0) {
      return [];
    }
    const facilityId = this.costFacilityId || this.selectedFacilityId;
    if (!facilityId) { return []; }
    return this.productionRates.getQuantityBreaks(facilityId, this.quantity);
  }

  /** Unit cost of the current `cost` (cost minus perOrderCost flat, divided by quantity).
   *  Returns 0 when rates aren't loaded or quantity is 0. */
  getUnitCost(): number {
    if (this.quantity === 0 || !this.productionRates || this.productionRatesStatus !== 'READY') {
      return 0;
    }
    const facilityId = this.costFacilityId || this.selectedFacilityId;
    if (!facilityId) { return 0; }
    const r = this.productionRates.getCostForFacility(facilityId, this.quantity);
    return r ? r.unitCost : 0;
  }

  /** Per-order flat cost that's added once regardless of quantity. */
  getPerOrderCost(): number {
    if (this.quantity === 0 || !this.productionRates || this.productionRatesStatus !== 'READY') {
      return 0;
    }
    const facilityId = this.costFacilityId || this.selectedFacilityId;
    if (!facilityId) { return 0; }
    const r = this.productionRates.getCostForFacility(facilityId, this.quantity);
    return r ? r.perOrderCost : 0;
  }

  /** Facility ids that can produce or ship this item at its current quantity. */
  getProducibleFacilityIds(): number[] {
    if (!this.productionRates) { return []; }
    return this.productionRates.getProducibleFacilityIds(this.quantity);
  }

  /** Facility ids that have enough inventory on hand for the current quantity. */
  getInventoryFacilityIds(): number[] {
    if (!this.productionRates) { return []; }
    return this.productionRates.getInventoryFacilityIds(this.quantity);
  }

  /**
   * User-facing pricing state for this line. See {@link ItemPricingState}.
   * Consumed by cart-page's per-line price template (spinner /
   * "Price unavailable" / retry) and by the checkout-button enable
   * gate — the button is enabled only when every non-zero line is
   * `READY`.
   */
  getPricingState(): ItemPricingState {
    if (this.quantity === 0) { return 'ZERO'; }
    // Fetch never fired or still in flight — surface as pending so
    // the user sees a spinner instead of a misleading $0.00.
    if (this.productionRatesStatus === 'NONE' || this.productionRatesStatus === 'LOADING') {
      return 'PENDING';
    }
    // Fetch errored on the wire — the rate table object is empty (see
    // OrderService.getProductionRatesForItem's catchError branch,
    // which resolves with a bare table so the observable completes).
    // Treat as ERROR so the UI offers a Retry button.
    if (this.productionRatesStatus === 'ERROR') { return 'ERROR'; }
    // From here down, status === READY. Determine whether the current
    // quantity actually resolves to a numeric price.
    if (!this.productionRates || !this.productionRates.facilities
        || this.productionRates.facilities.length === 0) {
      // Empty facility list — no rate document applied to any facility
      // for this item. Nothing the user can do at any quantity.
      return 'ERROR';
    }
    // Does any facility PRODUCE at the current quantity AND price it?
    // We check both "canPrint OR has inventory" AND "a break covers
    // this quantity". `getCheapestFacility` returns null when neither
    // condition is met at every facility.
    const cheapest = this.productionRates.getCheapestFacility(this.quantity);
    if (cheapest) { return 'READY'; }
    // No facility can price this specific quantity. Distinguish
    // "table is truly empty — no ranges exist anywhere" (ERROR — user
    // has no recourse from this line) from "table has ranges just not
    // this one" (INFEASIBLE — user can adjust quantity to fix).
    if (this.productionRates.hasAnyFeasibleQuantity()) { return 'INFEASIBLE'; }
    return 'ERROR';
  }

  /**
   * Feasible quantity ranges across every facility, cheapest-price
   * variant per range. Surfaced by the cart's per-line block when
   * `getPricingState()` returns `INFEASIBLE` so the user can see
   * "try 1-4 or 10+" instead of just "price unavailable".
   *
   * Returns an empty array when rates aren't loaded, no ranges exist
   * anywhere (the `ERROR` state), or the current quantity is already
   * priced (`READY` — the caller has no reason to display alternate
   * ranges). Otherwise returns the union of authored ranges collapsed
   * across facilities.
   */
  getFeasibleQuantityRanges(): Array<{
    minQty: number;
    maxQty: number;
    unitCost: number;
    perOrderCost: number;
  }> {
    if (!this.productionRates || this.productionRatesStatus !== 'READY') { return []; }
    const state = this.getPricingState();
    if (state !== 'INFEASIBLE') { return []; }
    return this.productionRates.getAllFeasibleRanges();
  }

  /**
   * True when every property the production-rate + shipping payloads
   * will read (`pages`, `interiorType`, `bindingType`, `laminateType`,
   * `width`, `height`, `weight`, `thickness`, `paperType`,
   * `caseQuantity`) can be sourced from data that has actually
   * arrived on the client.
   *
   * Default is `true` as a fail-open — legacy Item subclasses without
   * async link chains report ready. Subclasses whose payload sources
   * are attached asynchronously override this to gate the request:
   *
   *   - {@link DraftItem} — `pages`, `width`, `height`, `paperType`
   *     live on `draft.draftBody`; `laminateType` lives on
   *     `draft.draftCover`. Both attach through `DraftService.getLinkData`.
   *   - {@link ProductItem} — accurate `thickness`, `paperType`,
   *     `laminateType` (and precise `width`/`height`/`pages`/`weight`)
   *     live on `product.book.body` / `product.book.cover`. Attach
   *     chain is `ProductService → BookService → BodyService/CoverService`
   *     (two levels of async).
   *
   * Callers ({@link OrderService.getProductionRatesForItem} and
   * {@link PrintNodeOrderOption.compute}) short-circuit when this
   * returns `false` and re-attempt once the linked data arrives via
   * per-service `getAddNotifier` subscriptions on the cart page.
   */
  hasCompleteRateInputs(): boolean {
    return true;
  }

  abstract getId(): number;
  abstract getBase(): any;
  abstract getCoverImage(): string;
  abstract getTitle(): string;
  abstract getPages(): number;
  abstract getInteriorType(): string;
  abstract getBindingType(): string;
  abstract getLaminateType(): string;
  abstract getWidth(): number;
  abstract getHeight(): number;
  abstract getWeight(): number;
  abstract getThickness(): number;
  abstract getCaseQuantity(): number;
  abstract getPaperType(): number;
  abstract getLastUpdate(): Date;
  abstract getAvailableInventory(): any[];
}

